import {CURRENT_SCHEMA_VERSION} from './quoteTemplateSchema';

export type MigrationStep = (older: Record<string, unknown>) => Record<string, unknown>;

/**
 * One pure (older) => newer function per version bump, indexed by the version it upgrades FROM.
 *
 * Append-only: a step is frozen the moment it ships, because editing one rewrites history for
 * every row already stored.
 *
 * Empty by design — the current shape IS v1, so there is no older payload to upgrade from. The
 * machinery exists now so the first real shape change is a one-line append rather than a design.
 */
export const STEPS: readonly MigrationStep[] = [];

/**
 * Walks a stored payload forward. `steps` is a parameter rather than a closed-over constant so the
 * chain is testable with synthetic steps before a real one exists.
 *
 * A payload NEWER than the chain reaches skips it entirely: a rolled-back deploy meeting a payload
 * written by the newer one keeps the keys this build knows and drops the rest, which beats
 * discarding the user's content. Running a step written for an older shape against a newer one
 * would corrupt it.
 */
export function migrateTemplate(
	raw: Record<string, unknown>,
	steps: readonly MigrationStep[] = STEPS
): Record<string, unknown> {
	// A payload with no version predates versioning, which can only mean v1. Anything below the
	// first version, or not a whole number, reads as v1 too: the chain is indexed from 1, so a
	// stored 0 would reach for a step at index -1 and throw. The company service stores this blob
	// opaquely, so nothing upstream guarantees the number is one this build ever wrote.
	const claimed = typeof raw.schemaVersion === 'number' ? raw.schemaVersion : 1;
	const stored = Number.isInteger(claimed) && claimed >= 1 ? claimed : 1;
	// The real chain targets the version this build understands; a synthetic one targets whatever
	// its own length reaches, so a test does not have to move CURRENT_SCHEMA_VERSION to exercise it.
	const target = steps === STEPS ? CURRENT_SCHEMA_VERSION : steps.length + 1;
	if (stored >= target) {
		return raw;
	}
	let payload = raw;
	for (let version = stored; version < target; version++) {
		payload = steps[version - 1](payload);
	}
	return {...payload, schemaVersion: target};
}
